<template>
  <div class="account-editor">
    <div class="mt-1">
      <div class="d-flex-inline full-width accounts-container">
        <sera-input
          ref="accountNameInput"
          name="accountName"
          class="flex-1"
          :value="inputValue"
          :error="!!formError"
          :disabled="inputDisabled"
          @change="handleTextChange"
        >
          <template #label>
            <div>
              <span class="field-label">{{ fieldLabel }}:</span>
              <Tooltip v-if="tooltipText" width="300" placement="top">
                <template #trigger>
                  <div class="d-flex align-items-center">
                    <i class="ml-1 fas fa-question-circle"></i>
                  </div>
                </template>
                <template #tooltip>
                  <div class="text-center">
                    {{ tooltipText }}
                  </div>
                </template>
              </Tooltip>
            </div>
          </template>
        </sera-input>
        <div v-if="!saving">
          <div v-if="editing">
            <SeraButton class="ml-2" text="Save" @click="handleSave" />
            <SeraButton
              type="secondary"
              class="ml-1"
              text="Cancel"
              @click="handleCancel"
            />
          </div>
          <div v-else>
            <SeraButton
              class="edit-button ml-2"
              icon="fa-regular fa-pencil"
              text="Edit"
              @click="editAccount"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import SeraButton from "@/components/Button.vue";
import Tooltip from "@/components/Tooltip.vue";
import { injectUseQuickbooksConfig } from "./useQuickbooksConfig";

const props = defineProps({
  companyId: {
    type: Number,
    default: -1,
    required: true
  },
  fieldName: {
    type: String,
    default: "",
    required: true
  },
  fieldLabel: {
    type: String,
    default: "",
    required: true
  },
  tooltipText: {
    type: String,
    default: "",
    required: false
  },
  accountName: {
    type: String,
    default: "",
    required: false
  }
});

const editing = ref(false);
const saving = ref(false);
const accountName = ref(props.accountName);
const accountNameInput = ref<any>(null);
const formError = ref(props.accountName ? "" : "Required");
const { saveAccount } = injectUseQuickbooksConfig();

function editAccount() {
  editing.value = true;
  setTimeout(() => {
    accountNameInput.value.focus();
  });
}

const inputDisabled = computed(() => {
  return saving.value || !editing.value;
});

const inputValue = computed(() => {
  if (saving.value) {
    return "Saving...";
  }

  if (accountName.value) {
    return accountName.value;
  }

  return editing.value ? "" : "None";
});

function handleTextChange({ detail }) {
  accountName.value = detail;
}

async function handleSave() {
  saving.value = true;
  formError.value = "";

  try {
    const company = await saveAccount(
      props.companyId,
      props.fieldName,
      accountName.value
    );

    accountName.value = company[`${props.fieldName}_name`];

    window.notify.success(`${props.fieldLabel} saved successfully!`);

    editing.value = false;
  } catch (e: any) {
    formError.value = e.message;
  }
  saving.value = false;
}

function handleCancel() {
  formError.value = props.accountName ? "" : "Required";
  accountName.value = props.accountName;
  editing.value = false;
}
</script>

<style lang="scss" scoped>
.account-editor {
  .field-label {
    font-weight: 600;
    font-size: 14px;
  }

  .accounts-container {
    align-items: flex-end;
  }

  .info {
    color: var(--sera-color-core-blue-mid-blue-500);
  }
}
</style>
